import { GraphInputHelpers, ONE_DAY_MS } from '@/fetching/graphql/GraphInputHelpers';
import { VisitDatetimeField, VisitOutcome } from '@/types/visit';
import { EpisodeStatus, Ordering, PatientFilter, VisitFilter } from 'gql-types';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import {
    DEFAULT_CONTAINER_FILTER_WINDOW_DAYS,
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import {
    EpisodeOfCareDashboardContainer,
    PatientDashboardContainer,
    RootEntity,
    VisitDashboardContainer,
} from '../types';
import { PENN_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { HH_OVERDUE_VISITS_CONTAINER, HH_PATIENT_NO_SHOWS_CONTAINER } from './homeHealth';
import { IDashboardVariationConfig } from './types';

// ---------------------------------------------------------------------------
// Penn Medicine Dashboard WorkQueues
// https://docs.google.com/document/d/1RBlAb2YkBrFyIVZe4yy58PraznwKm84H/edit

// === TODO() ervice codes Penn to populate =====================================

// Service codes that map to SOCs.
const SOC_SERVICE_IDS: string[] = [];

// Service codes that map to ROCs and ROC + Recert.
const ROC_SERVICE_IDS: string[] = [];

// Service codes that map to Evals.
const EVAL_SERVICE_IDS: string[] = [];

// Service codes that resolve an on-hold (transferred) episode and therefore
// should NOT be flagged as "pending visits to cancel" on the Transferred
// Patients WQ.
const TRANSFER_RESOLUTION_SERVICE_IDS: string[] = [];

// Service codes that map to OASIS Discharge (Agency Discharged WQ).
const OASIS_DISCHARGE_SERVICE_IDS: string[] = [];

// Service codes that map to OASIS Recert or OASIS Discharge (Conflicting WQ).
const OASIS_RECERT_SERVICE_IDS: string[] = [];

// Service codes that map to Recert or Discharge (Upcoming End of Episode WQ).
const RECERT_OR_DISCHARGE_SERVICE_IDS: string[] = [];

// Service codes that map to Discipline Discharges, per discipline.
const OT_DISCIPLINE_DISCHARGE_SERVICE_IDS: string[] = [];
const PT_DISCIPLINE_DISCHARGE_SERVICE_IDS: string[] = [];
const SN_DISCIPLINE_DISCHARGE_SERVICE_IDS: string[] = [];
const ST_DISCIPLINE_DISCHARGE_SERVICE_IDS: string[] = [];
const MSW_DISCIPLINE_DISCHARGE_SERVICE_IDS: string[] = [];

const ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    ...OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...SN_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...ST_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...MSW_DISCIPLINE_DISCHARGE_SERVICE_IDS,
];

// Service codes that map to HHA visits + the supervisory visits that cover them.
const HHA_VISIT_SERVICE_IDS: string[] = [];
const HHA_SUPERVISORY_SERVICE_IDS: string[] = [];

// PTA / OTA visits + the PT / OT supervisory visits that cover them.
const PTA_VISIT_SERVICE_IDS: string[] = [];
const OTA_VISIT_SERVICE_IDS: string[] = [];
const PT_THERAPY_SUPERVISORY_SERVICE_IDS: string[] = [];
const OT_THERAPY_SUPERVISORY_SERVICE_IDS: string[] = [];

// Service codes that map to Reassessments or Recerts (Pending Re-Eval WQ).
const REASSESSMENT_SERVICE_IDS: string[] = [];

// Admin / non-billable items that should NOT count as "pending visits needing
// scheduler action" on the post-discharge / post-transfer queues.
const POST_DISCHARGE_EXCLUDED_SERVICE_IDS: string[] = [];

// === Containers ============================================================

// Incomplete Care Team
// Inclusion Criteria
// 1. A patient has an active order in a discipline for which there is no active clinician on the patient's care team
// Sorting Logic
// Patients are sorted by number of days since care team assignment was needed, with the oldest first
const INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = {
    key: PENN_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Team',
    defaultEnabled: true,
    hoverText: 'Patients that need care teams assigned.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
                endDate: { gte: GraphInputHelpers.date(new Date()) },
                hasVisits: {
                    disciplineCoveredByCareTeam: { exact: false },
                    outcome: { exact: VisitOutcome.NONE },
                    NOT: {
                        disciplines: {
                            externalIds: { value: { exact: 'MISC' } },
                        },
                    },
                },
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients with incomplete care teams.',
        // TODO(sorting): "days since care team assignment was needed, oldest
        // first" needs a derived BE field; fullName ASC placeholder.
        ordering: { fullName: Ordering.ASC },
    },
};

// SOC (Start of Care)
// Inclusion Criteria
// 1. A visit has a service code in the list below
// 2. That visit is missing one of the following: clinician or date
// Sorting Logic
// Sorted by Days left in the fulfillment window, with lowest number first
const SOC_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.soc,
    label: 'SOC (Start of Care)',
    defaultEnabled: false,
    hoverText: 'SOCs that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
            needsAssignment: { exact: true },
            episode: {
                status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
                endDate: { gte: GraphInputHelpers.date(new Date()) },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No SOC visits to schedule.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// ROC (Resumption of Care)
// Inclusion Criteria
// 1. A visit has a service code in the list below
// 2. That visit is missing one of the following: clinician or date
// Sorting Logic
// By number of days left in fulfillment window, with lowest number first
const ROC_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.roc,
    label: 'ROC (Resumption of Care)',
    defaultEnabled: false,
    hoverText: 'ROCs that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
            needsAssignment: { exact: true },
            episode: {
                status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.ON_HOLD] },
                endDate: { gte: GraphInputHelpers.date(new Date()) },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No ROC visits to schedule.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Eval
// Inclusion Criteria
// 1. A visit has a service code in the list below
// 2. That visit is missing one of the following: clinician or date
// Sorting Logic
// Sorted by the number of days remaining in the Eval window, with the lowest number first.
const EVAL_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.eval,
    label: 'Eval',
    defaultEnabled: false,
    hoverText: 'Evals that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
            needsAssignment: { exact: true },
            episode: {
                status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
                endDate: { gte: GraphInputHelpers.date(new Date()) },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No Eval visits to schedule.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Overdue Visits
// Inclusion Criteria
// 1. A visit has outcome = 'None'
// 2. The visit tentative date is before today
// Sorting Logic
// Sorted by visit date/time, farthest from the current date/time first.
const OVERDUE_VISITS_CONTAINER = {
    ...HH_OVERDUE_VISITS_CONTAINER,
    key: PENN_CONTAINER_KEYS.overdueVisits,
    snoozable: true,
    tableConfig: {
        ...HH_OVERDUE_VISITS_CONTAINER.tableConfig,
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Potential Reschedules
// Inclusion Criteria
// 1. A visit has outcome = "Patient did not show" and state = "In Review"
// Sorting Logic
// Sorted by visit date/time, closest to the current date/time first.
const POTENTIAL_RESCHEDULES_CONTAINER: VisitDashboardContainer = {
    ...HH_PATIENT_NO_SHOWS_CONTAINER,
    key: PENN_CONTAINER_KEYS.potentialReschedules,
    label: 'Potential Reschedules',
    hoverText: 'Visits that need to be either rescheduled or marked as missed.',
    snoozable: true,
    tableConfig: {
        ...HH_PATIENT_NO_SHOWS_CONTAINER.tableConfig,
        ordering: { effectiveSortDate: Ordering.DESC },
    },
};

// Urgent Visits Needing Scheduling
// Inclusion Criteria
// 1. A visit is missing one of the following: service, clinician, or date
// 2. The visit is within 7 days of the fulfillment window end date
// Sorting Logic
// Sorted by time until the fulfillment window end date, with the closest date first.
const URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.urgentVisitsNeedingScheduling,
    label: 'Urgent Visits Needing Scheduling',
    defaultEnabled: true,
    hoverText: 'Visits that need to be scheduled within the next 7 days.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: true } },
            episode: {
                status: { exact: EpisodeStatus.ACTIVE },
                isActiveByDate: { exact: true },
            },
            needsAssignment: { exact: true },
            latestEndDatetime: {
                gte: GraphInputHelpers.date(new Date()),
                lte: GraphInputHelpers.date(new Date(Date.now() + 7 * ONE_DAY_MS)),
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits with expiring fulfillment windows.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Patients Needing Scheduling
// Inclusion Criteria
// 1. A patient has at least one visit missing one of the following: service, clinician, or date
// Sorting Logic
// Sorted by fulfillment window end date, with the soonest window end date first.
const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PENN_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    defaultEnabled: true,
    hoverText: 'Patients with visits that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            status: { exact: EpisodeStatus.ACTIVE },
            isActiveByDate: { exact: true },
            hasVisits: {
                needsAssignment: { exact: true },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No patients to schedule.',
        // TODO(sorting): "fulfillment window end date, soonest first".
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Transferred Patients With Pending Visits
// Inclusion Criteria
// 1. A patient's episode status = 'On Hold'
// 2. The patient has future visits with outcome = 'None'
// Sorting Logic
// Sorted by earliest date of upcoming visits.
const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.transferredPatientsWithPendingVisits,
    label: 'Transferred Patients with Pending Visits',
    defaultEnabled: false,
    hoverText: 'Transferred patients with scheduled visits that need to be cancelled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: true } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
            episode: { status: { exact: EpisodeStatus.ON_HOLD } },
            NOT: {
                services: {
                    externalIds: {
                        value: {
                            inList: [
                                ...TRANSFER_RESOLUTION_SERVICE_IDS,
                                ...POST_DISCHARGE_EXCLUDED_SERVICE_IDS,
                            ],
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No transfers requiring attention.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Agency Discharged Patients With Pending Visits
// Inclusion Criteria
// 1. A patient is not active
// 2. The patient has scheduled future visits with outcome = 'None'
// Sorting Logic
// Sorted by earliest date of upcoming visits.
const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.agencyDischargedPatientsWithPendingVisits,
    label: 'Agency Discharged Patients with Pending Visits',
    defaultEnabled: false,
    hoverText: 'Agency discharged patients with scheduled visits that need to be cancelled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: false } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
            NOT: {
                services: {
                    externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
                },
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No agency discharged patients with pending visits.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Discipline Discharge Patients With Pending Visits
// Inclusion Criteria
// 1. A patient has been marked as discharged for a specific discipline
// 2. The patient has visits with outcome = 'None' for that discipline
// Sorting Logic
// Sorted by earliest date of upcoming visits
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    defaultEnabled: false,
    hoverText: 'Discipline discharged patients with scheduled visits that need to be canceled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            NOT: {
                services: {
                    externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
                },
            },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                sameDiscipline: true,
                filter: {
                    services: {
                        externalIds: { value: { inList: ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS } },
                    },
                    outcome: { exact: VisitOutcome.COMPLETED },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        ltOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No pending visits after discipline discharge.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Discontinued Frequencies With Pending Visits
// Inclusion Criteria
// 1. A patient's frequency order or PRN order has been Discontinued
// 2. The patient has visits tied to that frequency with outcome = 'None'
// Sorting Logic
// Sorted by earliest date of upcoming visits
// NOTE: no service-code dependency.
const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits,
    label: 'Discontinued Frequencies with Pending Visits',
    defaultEnabled: true,
    hoverText: 'Discontinued frequencies with scheduled visits that need to be cancelled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: true } },
            outcome: { exact: VisitOutcome.NONE },
            relatedOrder: {
                discontinuedAt: { lt: GraphInputHelpers.date(new Date()) },
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No visits tied to discontinued frequencies.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Inactive Clinicians
// Inclusion Criteria
// 1. Visits have outcome = 'None' and are assigned to a Clinician who is inactive
// Sorting Logic
// Sorted by visit dates with the earliest visit first
// NOTE: no service-code dependency.
const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    defaultEnabled: true,
    hoverText: 'Visits assigned to an inactive clinician.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            assignedClinicians: { isActive: { exact: false } },
            episode: {
                status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
                endDate: { gte: GraphInputHelpers.date(new Date()) },
            },
            effectiveDate: {
                gte: GraphInputHelpers.date(
                    new Date(Date.now() - DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No visits assigned to inactive clinicians.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Matches a future scheduled discharge (OASIS Discharge or per-discipline
// Discharge Summary) in the given therapy discipline. Used to exclude patients
// already on their way out from the supe / re-eval queues.
const THERAPY_DISCIPLINE_DISCHARGE_SERVICE_IDS_MAP: Record<'PT' | 'OT' | 'ST', string[]> = {
    PT: PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    OT: OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ST: ST_DISCIPLINE_DISCHARGE_SERVICE_IDS,
};

const pendingDischargeVisitFilter = (disciplineCode: 'PT' | 'OT' | 'ST'): VisitFilter => ({
    services: {
        externalIds: {
            value: {
                inList: [
                    ...OASIS_DISCHARGE_SERVICE_IDS,
                    ...THERAPY_DISCIPLINE_DISCHARGE_SERVICE_IDS_MAP[disciplineCode],
                ],
            },
        },
    },
    disciplines: { code: { exact: disciplineCode } },
    outcome: { exact: VisitOutcome.NONE },
    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
});

// Per-discipline branch for the Pending Re-Eval queue. We OR three of these
// (PT/OT/ST) at the top level so a patient lights up if ANY one discipline is
// missing coverage.
const pendingReEvalBranchForDiscipline = (disciplineCode: 'PT' | 'OT' | 'ST'): PatientFilter => ({
    isActive: { exact: true },
    hasEpisodes: {
        status: { exact: EpisodeStatus.ACTIVE },
        isActiveByDate: { exact: true },
    },
    // Anchor: patient has a future, pending visit in this discipline.
    hasVisits: {
        disciplines: { code: { exact: disciplineCode } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
    },
    AND: {
        // No completed reassessment in same discipline in the last 23 days.
        NOT: {
            hasVisits: {
                services: { externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } } },
                disciplines: { code: { exact: disciplineCode } },
                outcome: { exact: VisitOutcome.COMPLETED },
                effectiveDate: {
                    gte: GraphInputHelpers.date(new Date(Date.now() - 23 * ONE_DAY_MS)),
                },
            },
        },
        AND: {
            // No future scheduled, fully-assigned reassessment in this
            // discipline whose effectiveDate is within 30 days AFTER a prior
            // reassessment in the same discipline.
            NOT: {
                hasVisits: {
                    services: { externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } } },
                    disciplines: { code: { exact: disciplineCode } },
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                    needsAssignment: { exact: false },
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        filter: {
                            services: {
                                externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } },
                            },
                            disciplines: { code: { exact: disciplineCode } },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gteOffsetMinutes: -30 * 24 * 60,
                                lteOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
            AND: {
                // No pending discharge in this discipline.
                NOT: { hasVisits: pendingDischargeVisitFilter(disciplineCode) },
            },
        },
    },
});

// Shared "pending supervisory" filter shape used by the HHA and Therapy
// supervisory queues. A patient appears if all of the following:
//   1. They have a future, pending anchor visit (outcome = None, future).
//   2. They have NO completed supervisory in the look-back window.
//   3. They have NO future scheduled, fully-assigned supervisory whose
//      effectiveDate is within `lookaheadDays` AFTER a prior supervisory visit.
// `disciplineCode`, when provided, gates criteria 2 and 3 by `disciplines.code`.
const pendingSupervisoryBranch = ({
    anchorServiceIds,
    supervisoryServiceIds,
    lookbackDays,
    lookaheadDays,
    disciplineCode,
}: {
    anchorServiceIds: string[];
    supervisoryServiceIds: string[];
    lookbackDays: number;
    lookaheadDays: number;
    disciplineCode?: 'PT' | 'OT';
}): PatientFilter => {
    const disciplineFilter = disciplineCode
        ? { disciplines: { code: { exact: disciplineCode } } }
        : {};
    return {
        isActive: { exact: true },
        hasVisits: {
            services: { externalIds: { value: { inList: anchorServiceIds } } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
        },
        NOT: {
            hasVisits: {
                services: { externalIds: { value: { inList: supervisoryServiceIds } } },
                ...disciplineFilter,
                outcome: { exact: VisitOutcome.COMPLETED },
                effectiveDate: {
                    gte: GraphInputHelpers.date(new Date(Date.now() - lookbackDays * ONE_DAY_MS)),
                },
            },
        },
        AND: {
            NOT: {
                hasVisits: {
                    services: { externalIds: { value: { inList: supervisoryServiceIds } } },
                    ...disciplineFilter,
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                    needsAssignment: { exact: false },
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        sameEpisode: false,
                        filter: {
                            services: {
                                externalIds: { value: { inList: supervisoryServiceIds } },
                            },
                            ...disciplineFilter,
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gteOffsetMinutes: -lookaheadDays * 24 * 60,
                                lteOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
            ...(disciplineCode
                ? {
                      AND: {
                          NOT: { hasVisits: pendingDischargeVisitFilter(disciplineCode) },
                      },
                  }
                : {}),
        },
    };
};

// Pending HHA Supervisory Visits
// Inclusion Criteria
// 1. A patient has a future HHA visit in the service codes listed below with outcome = 'None'
// 2. A patient does not have a completed supervisory visit from the list of service codes below more recently than 5 days ago
// 3. There is no scheduled supervisory visit from the list of service codes below (not missing service, clinician, or date) within the next 5 days for that patient
// Sorting Logic
// Sorted by earliest date of upcoming supervisory visit
const PENDING_HHA_SUPERVISORY_CONTAINER: PatientDashboardContainer = {
    key: PENN_CONTAINER_KEYS.pendingHhaSupervisory,
    label: 'Pending HHA Supervisory Visits',
    defaultEnabled: false,
    hoverText: 'Patients needing an HHA Supervisory visit within the next 5 days.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            hasEpisodes: {
                status: { exact: EpisodeStatus.ACTIVE },
                isActiveByDate: { exact: true },
            },
            ...pendingSupervisoryBranch({
                anchorServiceIds: HHA_VISIT_SERVICE_IDS,
                supervisoryServiceIds: HHA_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 5,
                lookaheadDays: 5,
            }),
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling?episode=all`,
        emptyTableText: 'No patients pending HHA supervisory visits.',
    },
};

// Pending Therapy Supervisory Visits
// Inclusion Criteria
// 1. A patient has a future PTA or COTA visit in the service codes listed below with outcome = 'None'
// 2. The patient does not have a completed supervisory visit from the list of service codes below for that discipline more recently than 25 days ago
// 3. There is no scheduled supervisory visit (not missing service, clinician, or date) from the list of service codes below within the next 5 days for that patient
// Sorting Logic
// Sorted by days since the previous reassessment visit, with the highest number first
const PENDING_THERAPY_SUPERVISORY_CONTAINER: PatientDashboardContainer = {
    key: PENN_CONTAINER_KEYS.pendingTherapySupervisory,
    label: 'Pending Therapy Supervisory Visits',
    defaultEnabled: false,
    hoverText: 'Patients needing a PT or OT Supervisory visit within the next 5 days.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            ...pendingSupervisoryBranch({
                anchorServiceIds: PTA_VISIT_SERVICE_IDS,
                supervisoryServiceIds: PT_THERAPY_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 25,
                lookaheadDays: 5,
                disciplineCode: 'PT',
            }),
            OR: {
                ...pendingSupervisoryBranch({
                    anchorServiceIds: OTA_VISIT_SERVICE_IDS,
                    supervisoryServiceIds: OT_THERAPY_SUPERVISORY_SERVICE_IDS,
                    lookbackDays: 25,
                    lookaheadDays: 5,
                    disciplineCode: 'OT',
                }),
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling?episode=all`,
        emptyTableText: 'No patients pending therapy supervisory visits.',
    },
};

// Pending Therapy Re-Evaluation Visits
// Inclusion Criteria
// 1. A patient has a future visit associated with the PT, OT, or ST disciplines scheduled with outcome = 'None'
// 2. The patient does not have a completed visit from the list of service codes below for that discipline more recently than 25 days ago
// 3. There is no scheduled visit (not missing service, clinician, or date) from the list of service codes below within the next 5 days for that patient
// Sorting Logic
// Sorted by days since the previous reassessment visit, with the highest number first
const PENDING_THERAPY_REEVALUATION_CONTAINER: PatientDashboardContainer = {
    key: PENN_CONTAINER_KEYS.pendingTherapyReevaluation,
    label: 'Pending Therapy Re-Evaluation Visits',
    defaultEnabled: false,
    hoverText: 'Patients needing a PT, OT, or ST re-eval within the next 5 days.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            ...pendingReEvalBranchForDiscipline('PT'),
            OR: {
                ...pendingReEvalBranchForDiscipline('OT'),
                OR: {
                    ...pendingReEvalBranchForDiscipline('ST'),
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients pending re-evaluation.',
    },
};

// Upcoming End of Episode
// Inclusion Criteria
// 1. A patient's episode is in 'Active' status and ends 10 days from today or earlier (no lower bound)
// 2. There is no visit that has a service code which maps to a Recert or Discharge (listed below) that has been scheduled (not missing service, clinician, or date) or completed on that episode
// 3. OR there is no visit that has a service code which maps to a ROC (listed below) that has been scheduled or complete within the last 5 days of the episode
// Sorting Logic
// Sorted by days until the Episode End Date, with the lowest number first.
const UPCOMING_END_OF_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PENN_CONTAINER_KEYS.upcomingEndOfEpisode,
    label: 'Upcoming End of Episode',
    defaultEnabled: false,
    hoverText:
        'Patients nearing the end of their episode with no recert, discharge, or recent ROC visit scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            status: { exact: EpisodeStatus.ACTIVE },
            patient: { isActive: { exact: true } },
            endDate: {
                gte: GraphInputHelpers.date(new Date()),
                lte: GraphInputHelpers.date(new Date(Date.now() + 10 * ONE_DAY_MS)),
            },
            NOT: {
                hasVisits: {
                    services: {
                        externalIds: { value: { inList: RECERT_OR_DISCHARGE_SERVICE_IDS } },
                    },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
            AND: {
                NOT: {
                    hasVisits: {
                        services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
                        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                        effectiveDate: {
                            gte: GraphInputHelpers.date(new Date(Date.now() - 5 * ONE_DAY_MS)),
                            lte: GraphInputHelpers.date(new Date(Date.now() + 10 * ONE_DAY_MS)),
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes pending end of care.',
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Visits Scheduled Before SOC
// Inclusion Criteria
// 1. A patient has a SOC visit with an outcome = 'None' or 'Completed'
// 2. The patient has an any visit with an outcome = 'None' or 'Completed' with a date prior to that SOC
// Sorting Logic
// Sorted by date with soonest visits on top
const VISITS_SCHEDULED_BEFORE_SOC_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.visitsScheduledBeforeSoc,
    label: 'Visits Scheduled Before SOC',
    defaultEnabled: false,
    hoverText: "Visits that are scheduled or completed prior to the patient's SOC.",
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            episode: {
                status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
                endDate: { gte: GraphInputHelpers.date(new Date()) },
            },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                filter: {
                    services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gtOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits scheduled before SOCs.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Conflicting Discharge and Recert Visits
// Inclusion Criteria
// 1. An episode for a patient has more than 1 visit from the list of service codes below with outcome = 'None' or 'Completed''
// Sorting Logic
// Sorted by date of the second visit, with the soonest date first
const CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PENN_CONTAINER_KEYS.conflictingDischargeAndRecertVisits,
    label: 'Conflicting Discharge and Recert Visits',
    defaultEnabled: false,
    hoverText: 'Patients with more than 1 OASIS discharge or recert visit on an episode.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            status: { exact: EpisodeStatus.ACTIVE },
            isActiveByDate: { exact: true },
            hasVisits: {
                services: {
                    externalIds: {
                        value: {
                            inList: [...OASIS_DISCHARGE_SERVICE_IDS, ...OASIS_RECERT_SERVICE_IDS],
                        },
                    },
                },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                relatedVisitCondition: {
                    exists: true,
                    sameEpisode: true,
                    filter: {
                        services: {
                            externalIds: {
                                value: {
                                    inList: [
                                        ...OASIS_DISCHARGE_SERVICE_IDS,
                                        ...OASIS_RECERT_SERVICE_IDS,
                                    ],
                                },
                            },
                        },
                        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes with multiple OASIS Discharge or Recert visits.',
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Active Patients With No Visits
// Inclusion Criteria
// 1. A patient has status = 'Active' and has no visits with outcome = 'None'
// Sorting Logic
// Sorted by days since last visit with the longest duration of time since last visit first
// NOTE: no service-code dependency.
const ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER: PatientDashboardContainer = {
    key: PENN_CONTAINER_KEYS.activePatientsWithNoVisits,
    label: 'Active Patients with No Visits',
    defaultEnabled: true,
    hoverText: "Patients who are active but don't have any future visits scheduled.",
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                status: { exact: EpisodeStatus.ACTIVE },
                isActiveByDate: { exact: true },
            },
            NOT: {
                hasVisits: {
                    outcome: { exact: VisitOutcome.NONE },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No active patients without pending visits.',
    },
};

// Completed Visits Missing an Order
// Inclusion Criteria
// 1. A patient has a SOC/ROC/Eval visit with outcome = 'Completed' that does not have a linked order
// Sorting Logic
// Sorted by visits with the soonest SOC, ROC, or Eval first
const COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER: VisitDashboardContainer = {
    key: PENN_CONTAINER_KEYS.completedVisitsMissingAnOrder,
    label: 'Completed Visits Missing an Order',
    defaultEnabled: false,
    hoverText: "Completed SOCs/ROCs/Evals that aren't yet associated to an order.",
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.COMPLETED },
            hasOrder: { exact: false },
            patient: { isActive: { exact: true } },
            episode: {
                status: { exact: EpisodeStatus.ACTIVE },
                isActiveByDate: { exact: true },
            },
            services: {
                externalIds: {
                    value: {
                        inList: [...SOC_SERVICE_IDS, ...ROC_SERVICE_IDS, ...EVAL_SERVICE_IDS],
                    },
                },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

export const PENN_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        /* #1  */ INCOMPLETE_CARE_TEAM_CONTAINER,
        /* #2  */ SOC_CONTAINER,
        /* #3  */ ROC_CONTAINER,
        /* #4  */ EVAL_CONTAINER,
        /* #5  */ OVERDUE_VISITS_CONTAINER,
        /* #6  */ POTENTIAL_RESCHEDULES_CONTAINER,
        /* #7  */ URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER,
        /* #8  */ PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        /* #9  */ TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #10 */ AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #11 */ DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #12 */ DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER,
        /* #13 */ INACTIVE_CLINICIAN_VISITS_CONTAINER,
        /* #14 */ PENDING_HHA_SUPERVISORY_CONTAINER,
        /* #15 */ PENDING_THERAPY_SUPERVISORY_CONTAINER,
        /* #16 */ PENDING_THERAPY_REEVALUATION_CONTAINER,
        /* #17 */ UPCOMING_END_OF_EPISODE_CONTAINER,
        /* #18 */ VISITS_SCHEDULED_BEFORE_SOC_CONTAINER,
        /* #19 */ CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER,
        /* #20 */ ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER,
        /* #21 */ COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER,
    ],
};
